import Locations from '@/components/Locations/Locations/Locations';
import { HydraulicProfileUploadData } from '@/domain/HydraulicProfiler/HydraulicProfiler';
import HydraulicProfileService from '@/domain/HydraulicProfiler/HydraulicProfilerService';
import LocationService from '@/domain/Location/LocationService';
import { ArrowLeftOutlined, DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { Button, Upload, message, Modal, Input, notification } from 'antd';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import '../../ModelFiles/UploadModelFiles/index.scss';

const locationService = new LocationService();
const hydraulicProfileService = new HydraulicProfileService();
const { Dragger } = Upload;

interface ProfilerFile {
	name: string;
	size: number;
	type: string;
	jsonData: HydraulicProfileUploadData;
}

interface Location {
	id: number;
	name: string;
	description?: string;
}

function UploadProfilerFiles() {
	const navigate = useNavigate();
	const [messageApi, contextHolder] = message.useMessage();
	const [profilerFile, setProfilerFile] = useState<ProfilerFile[]>([]);
	const [locations, setLocations] = useState<Location[]>([]);
	const [selectLocation, setSelectLocation] = useState<number>();
	const [isProfileNameModalVisible, setIsProfileNameModalVisible] = useState<boolean>(false);
	const [profileName, setProfileName] = useState<string>('');
	const initialUploadProps: UploadProps = {
		name: 'file',
		multiple: true,
		accept: '.json',
		showUploadList: false,
		beforeUpload: (file: File) => {
			// Validate file name length
			if (file.name.length > 100) {
				const errorMessage = `File name ${file.name} exceeds 100 characters`;
				messageApi.error(errorMessage);
				notification.error({
					message: 'Error',
					description: errorMessage,
				});
				return false;
			}

			// Check file extension
			const fileExtension = file.name.split('.').pop()?.toLowerCase();
			if (fileExtension === 'json') {
				// Read the file content and parse it as JSON
				const reader = new FileReader();

				reader.onload = (e) => {
					try {
						const content = e.target?.result as string;
						const jsonData = JSON.parse(content);

						// Validate the JSON structure has required fields
						if (!jsonData.nodes || !jsonData.links || !jsonData.ground) {
							notification.error({
								message: 'Invalid File Format',
								description: 'The JSON file must contain nodes, links, and ground arrays.',
							});
							return;
						}

						// Show success message for valid JSON format
						notification.success({
							message: 'Valid File Format',
							description:
								'The JSON file format is correct. Please provide a unique name for this profile.',
						});

						// Store both the file metadata and parsed JSON content
						setProfilerFile([
							{
								name: file.name,
								size: file.size,
								type: file.type,
								jsonData: jsonData,
							},
						]);

						if (jsonData.config && jsonData.config.title) {
							setProfileName(jsonData.config.title);
						}
					} catch (error) {
						notification.error({
							message: 'Error',
							description: 'Invalid JSON format. Please check your file.',
						});
					}
				};

				reader.onerror = () => {
					notification.error({
						message: 'Error',
						description: 'Failed to read the file. Please try again.',
					});
				};

				reader.readAsText(file);
			} else {
				showErrorFile();
			}
			return false;
		},
	};

	/**
	 * Shows an error message for invalid file types
	 */
	const showErrorFile = () => {
		messageApi.error('Invalid file format');
		notification.error({
			message: 'Invalid File Format',
			description: 'Only JSON files are accepted. Please select a valid file.',
		});
	};

	/**
	 * Removes the currently selected file
	 */
	const deleteProfilerFile = () => {
		setProfilerFile([]);
	};

	/**
	 * Handles the upload of hydraulic profile data
	 * Validates required fields and sends data to the server
	 */
	const uploadFiles = async () => {
		// Check if a file has been selected
		if (profilerFile.length === 0) {
			openNotification();
			return;
		}

		// Validate location selection
		if (!selectLocation) {
			notification.error({
				message: 'Missing Location',
				description: 'You must select a location before uploading',
			});
			return;
		}

		// Extract the parsed JSON data from the file object
		const jsonData = profilerFile[0].jsonData;

		// Prepare the data for upload
		const data = { ...jsonData } as HydraulicProfileUploadData;

		// Configure the profile data with location information
		if (!data.config) {
			// Create config if it doesn't exist
			data.config = {
				title: profileName || profilerFile[0].name.replace('.json', ''),
				locationId: selectLocation,
				networkFile: profilerFile[0].name,
				x_axis: { title: 'Distance', min: 0, max: 100 },
				y_axis: { title: 'Elevation', min: 0, max: 100 },
				units: { x: 'm', z: 'm' },
			};
		} else {
			// Update config with user-provided title if available
			data.config.locationId = selectLocation!;
			if (profileName) {
				data.config.title = profileName;
				data.config.networkFile = profilerFile[0].name.replace('.json', '');
			}
		}

		try {
			messageApi.loading({ content: 'Uploading profile...', key: 'upload' });

			// Send data to server
			await hydraulicProfileService.uploadProfile(data);

			// Show success message
			messageApi.success({ content: 'Profile uploaded successfully', key: 'upload', duration: 2 });
			notification.success({
				message: 'Upload Complete',
				description: 'Hydraulic profile has been uploaded successfully',
			});

			// Navigate back to profiles list
			setTimeout(() => {
				navigate(`/upload?selectedTab=PROFILER`);
			}, 1000);
		} catch (error: any) {
			// Handle error
			messageApi.error({ content: 'Upload failed', key: 'upload', duration: 2 });
			notification.error({
				message: 'Upload Failed',
				description: error?.message || 'Could not upload the profile. Please try again.',
			});
			console.error('Error uploading profile:', error);
		}
	};

	/**
	 * Shows notification when no file is selected for upload
	 */
	const openNotification = () => {
		messageApi.error('No file selected');
		notification.error({
			message: 'No File Selected',
			description: 'Please select a JSON file to upload.',
		});
	};

	/**
	 * Fetches available locations for the current user
	 */
	const handleGetLocations = async () => {
		try {
			const userId = localStorage.getItem('userId') || '';
			const res = await locationService.getLocationsByUser(userId);
			setLocations(res);
		} catch (error) {
			console.error('Error fetching locations:', error);
			notification.error({
				message: 'Error',
				description: 'Failed to load locations. Please refresh the page.',
			});
		}
	};

	/**
	 * Updates the selected location when user makes a selection
	 * @param location - ID of the selected location
	 */
	const handleClickLocation = (location: number) => {
		setSelectLocation(location);
	};

	// File size function
	const fileSize = (size: number): string => {
		if (size === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(size) / Math.log(k));
		return `${parseFloat((size / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
	};

	// Add extension function
	const addExtension = (filename: string): string => {
		return filename.toLowerCase().endsWith('.json') ? filename : `${filename}.json`;
	};

	// Handle profile name input and start upload process
	const handleProfileNameSubmit = () => {
		if (!profileName.trim()) {
			notification.error({
				message: 'Profile Name Required',
				description: 'Please enter a unique name for this profile.',
			});
			return;
		}

		setIsProfileNameModalVisible(false);
		uploadFiles();
	};

	// Show modal to prompt for profile name
	const showProfileNameModal = () => {
		if (profilerFile.length === 0) {
			openNotification();
			return;
		}

		// Validate location selection
		if (!selectLocation) {
			notification.error({
				message: 'Missing Location',
				description: 'You must select a location before uploading',
			});
			return;
		}

		setIsProfileNameModalVisible(true);
	};

	useEffect(() => {
		handleGetLocations();
	}, []);

	return (
		<div style={{ padding: '0px 3rem' }}>
			{contextHolder}

			{/* Modal for entering profile name */}
			<Modal
				title="ENTER PROFILE NAME"
				open={isProfileNameModalVisible}
				destroyOnClose={true}
				onOk={handleProfileNameSubmit}
				onCancel={() => setIsProfileNameModalVisible(false)}
				okText="Upload File"
				cancelText="Cancel"
			>
				<div className="flex column gap-2">
					<p>Please enter a unique name for this hydraulic profile:</p>
					<Input
						value={profileName}
						onChange={(e) => setProfileName(e.target.value)}
						placeholder="Enter profile name"
						maxLength={100}
					/>
				</div>
			</Modal>
			<div>
				<Button
					type="text"
					shape="round"
					className="flex items-center"
					style={{ backgroundColor: '#F7F9FA', color: '#434B56' }}
					onClick={() => {
						navigate(`/upload?selectedTab=PROFILER`);
					}}
				>
					<ArrowLeftOutlined rev={undefined} /> Back to Profiler Files
				</Button>
			</div>
			<div className="flex column pt-2">
				<span className="text-title-page">Upload Profiler Files</span>
				<div
					className="mt-2 flex items-center"
					style={{
						background: 'rgba(255, 238, 203, 0.5)',
						height: '40px',
						borderLeft: 'solid 8px #FBC261',
					}}
				>
					<span className="ml-2" style={{ color: '#023E65' }}>
						Only JSON files accepted
					</span>
				</div>
				<div className="tableContainer mt-3 flex column w-full">
					<table
						style={{
							border: '2px solid #E6E6E6',
							height: 'calc(100vh - 340px)',
							borderRadius: '10px',
							borderCollapse: 'separate',
						}}
					>
						<thead>
							<tr style={{ height: '50px' }}>
								<th
									style={{
										paddingLeft: '20px',
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									File Name
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									Size
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								>
									Location
								</th>
								<th
									style={{
										borderBottom: '2px solid #E6E6E6',
									}}
								></th>
							</tr>
						</thead>
						{profilerFile.length === 0 ? (
							<tbody>
								<tr>
									<td colSpan={5}>
										<Dragger {...initialUploadProps} style={{ backgroundColor: 'white' }}>
											<div className="flex column" style={{}}>
												<span className="mb-2" style={{ color: '#74788D' }}>
													Drag and drop your files here or click in
												</span>
												{/* <Upload {...uploadProps}> */}
												<Button
													icon={<PlusCircleOutlined rev={undefined} />}
													shape="round"
													className="flex items-center self-center"
													style={{
														color: '#023E65',
														borderColor: '#023E65',
														backgroundColor: 'transparent',
													}}
												>
													Add Files
												</Button>
												{/* </Upload> */}
											</div>
										</Dragger>
									</td>
								</tr>
							</tbody>
						) : (
							<tbody>
								{profilerFile.length > 0 && (
									<tr style={{ height: '40px' }}>
										<td style={{ paddingLeft: '20px' }}>{profilerFile[0].name}</td>
										<td>{fileSize(profilerFile[0].size)}</td>
										<td className="pr-2">
											<Locations
												locations={locations}
												handleClickSelect={handleClickLocation}
												defaultValue={false}
											/>
										</td>
										<td>
											<DeleteOutlined
												style={{
													cursor: 'pointer',
													fontSize: '1.3rem',
												}}
												onClick={() => {
													deleteProfilerFile();
												}}
												rev={undefined}
											/>
										</td>
									</tr>
								)}

								<tr>
									<td colSpan={7}>
										<Dragger {...initialUploadProps} style={{ backgroundColor: 'white' }}>
											<Button
												icon={
													<PlusCircleOutlined
														style={{
															fontSize: '1.4rem',
														}}
														rev={undefined}
													/>
												}
												shape="round"
												style={{
													width: '50px',
													height: '50px',
													color: '#023E65',
													border: '2px solid #023E65',
													bottom: '10px',
													right: '10px',
												}}
												className="flex items-center justify-center absolute"
											/>
										</Dragger>
									</td>
								</tr>
							</tbody>
						)}
					</table>
				</div>
				<div className="flex pt-3 justify-between">
					<Button
						style={{ width: '85px', height: '40px' }}
						onClick={() => {
							navigate(`/upload?selectedTab=PROFILER`);
						}}
					>
						Cancel
					</Button>
					<div>
						<Button
							style={{
								width: '120px',
								height: '40px',
								backgroundColor: '#00203C',
								color: 'white',
							}}
							onClick={() => {
								showProfileNameModal();
							}}
						>
							Upload Profiler
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

function mapState(state: { locations: { array: any } }) {
	return {
		locations: state.locations.array,
	};
}

export default connect(mapState)(UploadProfilerFiles);
